import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Divider,
  List,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { notifications } from '@mantine/notifications';
import { PantryImageUpload } from './PantryImageUpload';
import { importPantryFromImages } from '../../services/pantryService';

interface ImportPantryModalProps {
  opened: boolean;
  onClose: () => void;
  onImportStarted: () => void;
}

interface ImportState {
  images: File[];
  error: string | null;
  importing: boolean;
}

export const ImportPantryModal: React.FC<ImportPantryModalProps> = ({
  opened,
  onClose,
  onImportStarted,
}) => {
  const [state, setState] = useState<ImportState>({
    images: [],
    error: null,
    importing: false,
  });
  const { user } = useAuth();

  const handleImagesSelected = useCallback((images: File[]) => {
    setState(prev => ({
      ...prev,
      images,
      error: null,
    }));
  }, []);

  const handleImagesCleared = useCallback(() => {
    setState(prev => ({
      ...prev,
      images: [],
      error: null,
    }));
  }, []);

  const handleReset = useCallback(() => {
    setState({
      images: [],
      error: null,
      importing: false,
    });
  }, []);

  const handleStartImport = useCallback(async () => {
    if (!user || state.images.length === 0) return;

    setState(prev => ({ ...prev, importing: true, error: null }));

    try {
      const token = await user.getIdToken();
      await importPantryFromImages(token, state.images);

      notifications.show({
        title: `${state.images.length} Pantry Image Import${state.images.length > 1 ? 's' : ''} Started`,
        message: `Your image${state.images.length > 1 ? 's are' : ' is'} being processed. Items will appear at the top when ready for review.`,
        color: 'blue',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

      // Close modal and trigger refresh
      onImportStarted();
      onClose();
      handleReset();

    } catch (error: any) {
      console.error('Pantry import error:', error);
      
      setState(prev => ({
        ...prev,
        importing: false,
        error: error.message || 'Failed to start pantry import. Please try again.',
      }));

      notifications.show({
        title: 'Import failed',
        message: error.message || 'Failed to start pantry import. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [user, state.images, onImportStarted, onClose, handleReset]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Import from pantry images</Text>}
      size="lg"
      centered
      radius="lg"
      closeOnClickOutside={!state.importing}
      closeOnEscape={!state.importing}
    >
      <Stack gap="md">
        {/* Error Display */}
        {state.error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Import error"
            color="red"
            variant="light"
            radius="lg"
          >
            {state.error}
          </Alert>
        )}

        {/* Image Upload */}
        <PantryImageUpload
          onImagesSelected={handleImagesSelected}
          onImagesCleared={handleImagesCleared}
          disabled={state.importing}
          showTips={true}
        />

        {/* Instructions */}
        {state.images.length === 0 && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="How it works"
            color="blue"
            variant="light"
            radius="lg"
          >
            <List spacing="xs" size="sm">
              <List.Item>Take photos of your pantry, fridge, or food storage areas</List.Item>
              <List.Item>Our AI will identify food items and add them to your pantry</List.Item>
              <List.Item>Imported items will be flagged for your review</List.Item>
            </List>
          </Alert>
        )}
        
        {/* Import Button */}
        {state.images.length > 0 && (
          <>
            <Divider />
            <Group justify="flex-end" mt="md">
              <Button
                onClick={handleStartImport}
                loading={state.importing}
                size="md"
                disabled={state.importing || state.images.length === 0}
              >
                {state.importing ? 'Processing Images...' : 'Import Items'}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
